import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Compass, Loader2, Sun, Thermometer, Wind } from 'lucide-react';
import { fetchPlantSensors, type SensorDetail } from '@/api/sensors';
import { useErrorToast, LoadRetry } from '@/lib/notify';

function reading(value: number | null | undefined, unit: string) {
  return value == null || !Number.isFinite(Number(value)) ? 'Veri Yok'
    : `${Number(value).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} ${unit}`;
}

export function SensorReadingsCard({ sensor }: { sensor: SensorDetail }) {
  const brandModel = [sensor.sensor_brand, sensor.sensor_model].filter(value => value?.trim() && value.trim().toLowerCase() !== 'other').join(' ');
  const metrics = [
    { label: 'Panel sıcaklığı', value: sensor.sensor_pv_temp, unit: '°C', Icon: Thermometer },
    { label: 'Ortam sıcaklığı', value: sensor.sensor_amp_temp, unit: '°C', Icon: Thermometer },
    { label: 'Işınım', value: sensor.sensor_radiation, unit: 'W/m²', Icon: Sun },
    { label: 'Rüzgar hızı', value: sensor.sensor_wind_speed, unit: 'm/s', Icon: Wind },
    { label: 'Rüzgar yönü', value: sensor.sensor_wind_direction, unit: '°', Icon: Compass },
    { label: 'Günlük ışınım', value: sensor.sensor_daily_irradiation_kwh, unit: 'kWh/m²', Icon: Sun },
  ];
  return <article className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <Link to={`/dashboard/sensors/${sensor.sensor_uuid_id}`} className="break-words text-sm font-semibold text-blue-600 hover:underline">{sensor.sensor_name}</Link>
        <p className="mt-1 text-xs text-slate-500">{brandModel || 'Marka / model belirtilmemiş'} · Seri no: {sensor.sensor_serial_number || '—'}</p>
      </div>
      <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${sensor.sensor_status === 1 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'}`}>
        {sensor.sensor_status === 1 ? 'Online' : 'Offline'}
      </span>
    </div>
    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
      <p>Son kayıt: {sensor.sensor_last_seen || 'Veri Yok'}</p>
      <p>FTP: {sensor.sensor_ftp_connection_status === 1 ? 'Bağlı' : 'Bağlı değil'} · Modbus: {sensor.sensor_modbus_connection_status === 1 ? 'Bağlı' : 'Bağlı değil'}</p>
    </div>
    {sensor.sensor_status !== 1 && sensor.sensor_last_seen && <p className="text-xs text-amber-800">Değerler anlık değildir; son kayıtlı ölçümler gösteriliyor.</p>}
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      {metrics.map(({ label, value, unit, Icon }) => <div key={label} className="min-w-0 rounded-xl border border-cyan-100 bg-cyan-50/40 p-3">
        <div className="flex items-center gap-1.5 text-xs text-slate-600"><Icon size={14} className="shrink-0 text-cyan-600" />{label}</div>
        <p className="mt-2 break-words text-base font-semibold text-slate-900 tabular-nums">{reading(value, unit)}</p>
      </div>)}
    </div>
  </article>;
}

export default function PlantSensorsPanel({ plantUuid }: { plantUuid: string }) {
  const [sensors, setSensors] = useState<SensorDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  useErrorToast(error ? 'Sensör verileri alınamadı.' : '');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(false); setSensors([]);
    fetchPlantSensors(plantUuid, controller.signal)
      .then(data => { if (!controller.signal.aborted) setSensors(data); })
      .catch(() => { if (!controller.signal.aborted) setError(true); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [plantUuid, retry]);
  if (loading) return <p role="status" className="flex items-center gap-2 p-4 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" />Sensörler yükleniyor…</p>;
  if (error) return <LoadRetry onRetry={() => setRetry(value => value + 1)} />;
  return <div className="space-y-4">{sensors.length ? sensors.map(sensor => <SensorReadingsCard key={sensor.sensor_id} sensor={sensor} />)
    : <p className="rounded-xl border border-dashed border-slate-200 p-6 text-sm text-slate-500">Bu santrale bağlı sensör bulunamadı.</p>}</div>;
}
